// src/pages/AdminPage.jsx
// Weekly menu rotation portal at /admin (not linked from any nav).
// Shared-password access: the password is sent as X-Admin-Key on every request
// and verified server-side (ADMIN_PORTAL_KEY). Team workflow each week:
//   1. toggle which dishes are active   2. adjust prices / names
//   3. Publish → storefront cache refreshes immediately.
// Ingredient composition and images still live in Airtable (they feed the
// portioning algorithm) — this page deliberately can't touch them.

import React, { useEffect, useMemo, useState } from 'react';
import { COLORS, FONTS } from 'styles/theme';

const API = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';
const { dark, cream, creamAlt, orange } = COLORS;

const MEAL_ORDER = ['Breakfast', 'Lunch', 'Dinner', 'Snack', 'Add On'];

const INPUT = {
  fontFamily: FONTS.sans, fontSize: '14px', padding: '8px 10px',
  border: '1px solid #d8d0bd', borderRadius: '8px', background: '#fff', color: dark,
};

async function adminFetch(key, path, options = {}) {
  const res = await fetch(`${API}/admin${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'X-Admin-Key': key,
      ...(options.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.success) {
    const err = new Error(data.error || `Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return data;
}

function DishRow({ dish, adminKey, onSaved, onError }) {
  const [draft, setDraft] = useState({ name: dish.name, price: dish.price ?? '' });
  const [saving, setSaving] = useState(false);
  const dirty = draft.name !== dish.name || String(draft.price) !== String(dish.price ?? '');

  const patch = async (changes) => {
    setSaving(true);
    try {
      await adminFetch(adminKey, `/menu/${dish.recordId}`, {
        method: 'PATCH',
        body: JSON.stringify(changes),
      });
      onSaved(dish.recordId, changes);
    } catch (e) {
      onError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const active = dish.status === 'active';

  return (
    <div style={{
      display: 'grid', gridTemplateColumns: '56px 1fr 110px 90px 120px 90px',
      gap: '12px', alignItems: 'center', padding: '10px 14px',
      background: '#fff', borderBottom: `1px solid ${creamAlt}`,
      opacity: active ? 1 : 0.55,
    }}>
      <div style={{
        width: '52px', height: '40px', borderRadius: '6px', background: creamAlt,
        backgroundImage: dish.imageUrl ? `url(${dish.imageUrl})` : 'none',
        backgroundSize: 'cover', backgroundPosition: 'center',
      }} />
      <div>
        <input
          style={{ ...INPUT, width: '100%' }}
          value={draft.name}
          onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
          aria-label={`Name for dish ${dish.dishId}`}
        />
        <div style={{ fontSize: '11px', color: '#8a8d91', marginTop: '2px' }}>
          Dish {dish.dishId} · {dish.mealType}
        </div>
      </div>
      <input
        style={{ ...INPUT, width: '100%' }}
        type="number"
        min="0"
        step="0.01"
        value={draft.price}
        onChange={(e) => setDraft((d) => ({ ...d, price: e.target.value }))}
        aria-label={`Price for ${dish.name}`}
      />
      <button
        type="button"
        disabled={saving || !dirty}
        onClick={() => patch({ name: draft.name.trim(), price: Number(draft.price) })}
        style={{
          ...INPUT, cursor: saving || !dirty ? 'default' : 'pointer',
          background: dirty ? orange : '#f2efe7', color: dirty ? '#fff' : '#999',
          border: 'none', fontWeight: 700,
        }}
      >
        {saving ? '…' : 'Save'}
      </button>
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={active}
          disabled={saving}
          onChange={() => patch({ status: active ? 'inactive' : 'active' })}
        />
        {active ? 'Active' : 'Inactive'}
      </label>
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', cursor: 'pointer' }} title="Exclude from subscriptions">
        <input
          type="checkbox"
          checked={dish.excludeFromSubscriptions}
          disabled={saving}
          onChange={() => patch({ excludeFromSubscriptions: !dish.excludeFromSubscriptions })}
        />
        No subs
      </label>
    </div>
  );
}

export default function AdminPage() {
  const [adminKey, setAdminKey] = useState(() => sessionStorage.getItem('sf_admin_key') || '');
  const [passwordInput, setPasswordInput] = useState('');
  const [authed, setAuthed] = useState(false);
  const [dishes, setDishes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [publishState, setPublishState] = useState(null); // null | 'busy' | {activeDishes}

  useEffect(() => { document.title = 'Menu Admin — Square Fare'; }, []);

  const load = async (key) => {
    setLoading(true);
    setError('');
    try {
      const data = await adminFetch(key, '/menu');
      setDishes(data.dishes);
      setAuthed(true);
      sessionStorage.setItem('sf_admin_key', key);
      setAdminKey(key);
    } catch (e) {
      if (e.status === 401) sessionStorage.removeItem('sf_admin_key');
      setAuthed(false);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (adminKey) load(adminKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sections = useMemo(() => {
    const byType = {};
    dishes.forEach((d) => {
      const key = MEAL_ORDER.includes(d.mealType) ? d.mealType : 'Other';
      (byType[key] = byType[key] || []).push(d);
    });
    return [...MEAL_ORDER, 'Other']
      .filter((t) => byType[t]?.length)
      .map((t) => ({ label: t, dishes: byType[t].sort((a, b) => a.name.localeCompare(b.name)) }));
  }, [dishes]);

  const activeCount = dishes.filter((d) => d.status === 'active').length;

  const onSaved = (recordId, changes) => {
    setDishes((prev) => prev.map((d) => {
      if (d.recordId !== recordId) return d;
      const next = { ...d };
      if ('name' in changes) next.name = changes.name;
      if ('price' in changes) next.price = changes.price;
      if ('status' in changes) next.status = changes.status;
      if ('excludeFromSubscriptions' in changes) next.excludeFromSubscriptions = changes.excludeFromSubscriptions;
      return next;
    }));
    setPublishState(null); // edits since last publish
  };

  const publish = async () => {
    setPublishState('busy');
    setError('');
    try {
      const data = await adminFetch(adminKey, '/publish', { method: 'POST' });
      setPublishState({ activeDishes: data.activeDishes });
    } catch (e) {
      setPublishState(null);
      setError(e.message);
    }
  };

  if (!authed) {
    return (
      <div style={{ minHeight: '100vh', background: cream, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: FONTS.sans }}>
        <form
          onSubmit={(e) => { e.preventDefault(); load(passwordInput); }}
          style={{ background: '#fff', border: `1px solid ${creamAlt}`, borderRadius: '14px', padding: '36px', width: '340px', textAlign: 'center' }}
        >
          <h1 style={{ fontFamily: FONTS.serif, fontSize: '26px', color: dark, margin: '0 0 6px' }}>Menu admin</h1>
          <p style={{ fontSize: '14px', color: '#77787f', margin: '0 0 20px' }}>Weekly menu rotation</p>
          <input
            style={{ ...INPUT, width: '100%', marginBottom: '12px', boxSizing: 'border-box' }}
            type="password"
            placeholder="Admin password"
            value={passwordInput}
            onChange={(e) => setPasswordInput(e.target.value)}
            autoFocus
          />
          {error && <p style={{ color: '#c0392b', fontSize: '13px', margin: '0 0 12px' }} role="alert">{error}</p>}
          <button type="submit" disabled={loading || !passwordInput} style={{
            ...INPUT, width: '100%', background: orange, color: '#fff', border: 'none',
            fontWeight: 700, cursor: 'pointer', boxSizing: 'border-box',
          }}>
            {loading ? 'Checking…' : 'Enter'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: cream, fontFamily: FONTS.sans, paddingBottom: '80px' }}>
      <header style={{
        position: 'sticky', top: 0, zIndex: 10, background: '#fff',
        borderBottom: `1px solid ${creamAlt}`, padding: '14px 4%',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap',
      }}>
        <div>
          <span style={{ fontFamily: FONTS.serif, fontSize: '20px', fontWeight: 600, color: dark }}>Menu admin</span>
          <span style={{ marginLeft: '14px', fontSize: '13px', color: '#77787f' }}>
            {activeCount} of {dishes.length} dishes active
          </span>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          {publishState && publishState !== 'busy' && (
            <span style={{ fontSize: '13px', color: '#2c5f2c' }} role="status">
              Live — {publishState.activeDishes} dishes on the site
            </span>
          )}
          <button type="button" onClick={() => load(adminKey)} disabled={loading} style={{ ...INPUT, cursor: 'pointer' }}>
            {loading ? 'Reloading…' : 'Reload'}
          </button>
          <button type="button" onClick={publish} disabled={publishState === 'busy'} style={{
            ...INPUT, background: orange, color: '#fff', border: 'none', fontWeight: 700, cursor: 'pointer',
          }}>
            {publishState === 'busy' ? 'Publishing…' : 'Publish to site'}
          </button>
          <button
            type="button"
            onClick={() => { sessionStorage.removeItem('sf_admin_key'); setAuthed(false); setAdminKey(''); setPasswordInput(''); }}
            style={{ ...INPUT, cursor: 'pointer' }}
          >
            Log out
          </button>
        </div>
      </header>

      {error && (
        <p style={{ color: '#c0392b', fontSize: '14px', padding: '12px 4% 0' }} role="alert">{error}</p>
      )}

      <main style={{ padding: '20px 4%' }}>
        <p style={{ fontSize: '13px', color: '#77787f', margin: '0 0 18px' }}>
          Toggle what's live this week, adjust prices and names, then Publish. Ingredients,
          dish composition and images are managed in Airtable.
        </p>

        {sections.map((section) => (
          <section key={section.label} style={{ marginBottom: '28px' }}>
            <h2 style={{ fontFamily: FONTS.serif, fontSize: '21px', color: dark, margin: '0 0 8px' }}>
              {section.label}
              <span style={{ fontSize: '13px', color: '#77787f', marginLeft: '10px', fontFamily: FONTS.sans }}>
                {section.dishes.filter((d) => d.status === 'active').length} active / {section.dishes.length}
              </span>
            </h2>
            <div style={{ border: `1px solid ${creamAlt}`, borderRadius: '12px', overflow: 'hidden' }}>
              <div style={{
                display: 'grid', gridTemplateColumns: '56px 1fr 110px 90px 120px 90px', gap: '12px',
                padding: '8px 14px', background: creamAlt, fontSize: '12px', fontWeight: 700, color: '#5a5b60',
              }}>
                <span></span><span>Dish</span><span>Price ($)</span><span></span><span>Status</span><span>Subs</span>
              </div>
              {section.dishes.map((dish) => (
                <DishRow
                  key={dish.recordId}
                  dish={dish}
                  adminKey={adminKey}
                  onSaved={onSaved}
                  onError={setError}
                />
              ))}
            </div>
          </section>
        ))}
      </main>
    </div>
  );
}
